import * as Api from '@ViewModels';
import { ESelectionState } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { parse } from 'query-string';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	IQuickAddEntityComponentProps,
	IUserSessionComponentProps,
	QuickAddEntityViewModelKey,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { useInput } from '../../../../models/hooks/inputHooks';
import { queryStringParamsToSearchRequest } from '../../../../models/UiUtils';
import { useCompaniesQuery } from '../../../../queries/Company/useCompaniesQuery';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Column, Row } from '../../../components-v2/PageLayout';
import { Button } from '../../../components/Button';
import { ROUTES } from '../../../constants/routes';
import { bs } from '../../../styles/styles';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { ContactsTableLoader } from '../shared/ContactsTableLoader';
import { CompaniesActionsBar } from './CompaniesActionsBar';
import {
	CompaniesSearchInputContext,
	CompaniesTab,
	CompaniesTabContext,
	CompanySearchContext,
	DefaultCompaniesFilter,
	DefaultCompaniesSort,
	getCompanySearchFromCriteria,
	getCompanyTabFromCriteria,
} from './CompaniesContext';
import { CompaniesGlobalSearch } from './CompaniesGlobalSearch';
import { CompaniesSearchBarTypeTabs } from './CompaniesSearchBarTypeTabs';
import { CompaniesSearchDescription } from './CompaniesSearchDescription';
import { CompaniesTable } from './CompaniesTable';
import { styleSheet } from './styles';

export const CompaniesContainer: React.FC<
	IQuickAddEntityComponentProps & IFullscreenModalComponentProps & IUserSessionComponentProps
> = inject(
	QuickAddEntityViewModelKey,
	FullScreenModalViewModelKey,
	UserSessionViewModelKey
)(
	observer(props => {
		const { quickAddEntity } = props;
		const location = useLocation();
		const history = useHistory();

		// Load search from query
		const params = parse(location?.search);
		const companiesQueryParams = queryStringParamsToSearchRequest<Api.ICompaniesSearchFilter>(params);
		const queryFilter: Api.ICompaniesSearchFilter = companiesQueryParams?.searchRequest || DefaultCompaniesFilter;
		const querySort = companiesQueryParams?.sortDescriptor || DefaultCompaniesSort;

		const [selectionState, setSelectionState] = React.useState<ESelectionState>(ESelectionState.None);
		const [selectedCompanyIds, setSelectedCompanyIds] = React.useState<string[]>([]);
		const [excludedCompanyIds, setExcludedCompanyIds] = React.useState<string[]>([]);

		// Filter Context
		const [filter, setFilter] = React.useState<Api.ICompaniesSearchFilter>(queryFilter);

		const [sort, setSort] = React.useState<Api.ISortDescriptor>(querySort);
		const companiesQuery = useCompaniesQuery({
			searchRequest: { searches: filter.criteria },
			sortDescriptor: sort,
		});

		const companies = React.useMemo(
			() => (companiesQuery.data?.pages?.flatMap(page => page.values) ?? []) as Api.ICompany[],
			[companiesQuery.data]
		);

		const filterValue = React.useMemo(
			() => ({
				companies,
				filter,
				setFilter,
				sort,
				setSort,
				selectionState,
				setSelectionState,
				selectedCompanyIds,
				setSelectedCompanyIds,
				excludedCompanyIds,
				setExcludedCompanyIds,
				companiesQuery,
			}),
			[
				filter,
				setFilter,
				sort,
				setSort,
				companies,
				selectionState,
				setSelectionState,
				selectedCompanyIds,
				setSelectedCompanyIds,
				excludedCompanyIds,
				setExcludedCompanyIds,
				companiesQuery,
			]
		);

		// Search Input Context

		const [search, setSearch, onSearchChange] = useInput(
			queryFilter?.criteria ? getCompanySearchFromCriteria(queryFilter.criteria) : ''
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		const searchValue = React.useMemo(() => ({ onSearchChange, search, setSearch }), [search]);

		// Tab Context

		const [tab, setTab] = React.useState<CompaniesTab>(
			queryFilter?.criteria ? getCompanyTabFromCriteria(queryFilter.criteria) : 'All'
		);
		const tabValue = React.useMemo(() => ({ setTab, tab }), [tab]);

		// Update states when query changes
		React.useEffect(() => {
			setFilter(queryFilter);
			setSort(querySort);

			if (!queryFilter?.criteria) return;
			setSearch(getCompanySearchFromCriteria(queryFilter.criteria));

			setTab(getCompanyTabFromCriteria(queryFilter.criteria));
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [location]);

		// "New Company" modal
		const onShowQuickAddContactModal = React.useCallback(() => {
			quickAddEntity.show({
				entity: {},
				onComplete: (error: Api.IOperationResultNoValue, companyModel?: Api.ICompany) => {
					if (!error && !!companyModel) {
						quickAddEntity.reset();
						history.push({
							pathname: `${ROUTES.contacts.companies}/${companyModel.id}`,
						});
					}
				},
				type: 'company',
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return (
			<>
				<CompaniesSearchInputContext.Provider value={searchValue}>
					<CompaniesTabContext.Provider value={tabValue}>
						<CompanySearchContext.Provider value={filterValue}>
							<Column styles={[bs.gap4, styleSheet.container]}>
								<Row styles={[styleSheet.searchRow]}>
									<Row styles={[styleSheet.searchControls]}>
										<CompaniesGlobalSearch />
										<CompaniesSearchBarTypeTabs />
									</Row>
									<Button
										label='New Company'
										className={css(styleSheet.newCompany)}
										onClick={onShowQuickAddContactModal}
									/>
								</Row>
								<div className={css(styleSheet.searchDescription)}>
									<CompaniesSearchDescription />
								</div>
								<CompaniesActionsBar />
								{companiesQuery.isPending && !companies.length ? <ContactsTableLoader /> : <CompaniesTable />}
							</Column>
						</CompanySearchContext.Provider>
					</CompaniesTabContext.Provider>
				</CompaniesSearchInputContext.Provider>
				<AppDrawer>
					<StickyAppDrawerContent value='ai-assistant'>
						<DefaultUniversalAssistantChat />
					</StickyAppDrawerContent>
					<AppDrawerToolbar>
						<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
					</AppDrawerToolbar>
				</AppDrawer>
			</>
		);
	})
);
